import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Loader2, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

interface ReminderSettingsProps {
  roadmapId: string | null;
  user: User | null;
  onSignInRequired: () => void;
}

const DAYS_OF_WEEK = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 7, label: "Sunday" },
];

export const ReminderSettings = ({
  roadmapId,
  user,
  onSignInRequired,
}: ReminderSettingsProps) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [reminderTime, setReminderTime] = useState("09:00");
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]); // Weekdays by default

  useEffect(() => {
    const loadSettings = async () => {
      if (!user || !roadmapId) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("user_reminders")
          .select("*")
          .eq("user_id", user.id)
          .eq("roadmap_id", roadmapId)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setIsActive(data.is_active);
          setReminderTime(data.reminder_time.slice(0, 5)); // HH:MM format
          setSelectedDays(data.days_of_week);
        }
      } catch (error) {
        console.error("Error loading reminder settings:", error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [user, roadmapId]);

  const handleSave = async () => {
    if (!user) {
      onSignInRequired();
      return;
    }

    if (!roadmapId) return;

    try {
      setSaving(true);

      const { error } = await supabase.from("user_reminders").upsert({
        user_id: user.id,
        roadmap_id: roadmapId,
        reminder_time: reminderTime,
        days_of_week: selectedDays,
        is_active: isActive,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      if (error) throw error;

      toast.success("Reminder settings saved! 🔔");
    } catch (error) {
      console.error("Error saving reminder settings:", error);
      toast.error("Failed to save reminder settings");
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()
    );
  };

  if (!user) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Daily Reminders
          </CardTitle>
          <CardDescription>
            Sign in to set up daily learning reminders
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={onSignInRequired} className="w-full">
            Sign In
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Daily Reminders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Daily Reminders
        </CardTitle>
        <CardDescription>
          Get email reminders to stay consistent with your learning
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable/Disable */}
        <div className="flex items-center justify-between">
          <Label htmlFor="reminder-active" className="text-base">
            Enable Reminders
          </Label>
          <Switch
            id="reminder-active"
            checked={isActive}
            onCheckedChange={setIsActive}
          />
        </div>

        {/* Time Selection */}
        <div className="space-y-2">
          <Label htmlFor="reminder-time">Reminder Time</Label>
          <input
            id="reminder-time"
            type="time"
            value={reminderTime}
            onChange={(e) => setReminderTime(e.target.value)}
            disabled={!isActive}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          />
          <p className="text-xs text-muted-foreground">
            Time is in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone})
          </p>
        </div>

        {/* Days Selection */}
        <div className="space-y-3">
          <Label>Reminder Days</Label>
          <div className="grid grid-cols-2 gap-3">
            {DAYS_OF_WEEK.map((day) => (
              <div key={day.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`day-${day.value}`}
                  checked={selectedDays.includes(day.value)}
                  onCheckedChange={() => toggleDay(day.value)}
                  disabled={!isActive}
                />
                <label
                  htmlFor={`day-${day.value}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {day.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <Button onClick={handleSave} disabled={saving || !isActive} className="w-full">
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>

        {isActive && (
          <div className="rounded-lg bg-primary/10 p-3 text-sm text-muted-foreground">
            <p className="font-medium mb-1">📧 You'll receive reminders via email</p>
            <p className="text-xs">
              Reminders are sent once per day at your selected time on the days you choose.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
